import { createContext, useContext } from 'react';
import type { PromptUploadRow } from '@/features/voice-campaigns/types';
import type { UserGroupAgentRow } from '@/features/voice-campaigns/types';
import type { SmsWebhookRow } from '@/features/webhooks/types';
import type { IvrEdge, IvrNode, IvrNodeData } from './types';

/**
 * Shared editor state passed to every node component. Avoids prop-drilling
 * through React Flow's per-node render.
 */
export interface IvrEditorContextValue {
  /** True when the flow is being viewed (not edited) — e.g. superadmin
   * "View Only" mode. Nodes stay draggable for layout purposes, but
   * settings/delete controls and connection editing must be hidden. */
  readOnly: boolean;
  /** All audio prompts available to this user (any category). */
  prompts: PromptUploadRow[];
  /** Agent groups for transfer nodes. */
  agentGroups: UserGroupAgentRow[];
  /** Whether the SMS Webhooks module is enabled — drives DTMF SMS picker. */
  smsWebhookFlag: boolean;
  smsWebhookList: SmsWebhookRow[];

  /** Live edges array — needed by nodes for "connections per handle" math. */
  edges: IvrEdge[];
  /** Lookup of `sourceHandle || sourceId` → list of target ids. */
  nodeConnections: Record<string, string[]>;

  /** Patch a node's `data` in place. */
  updateNodeData: (nodeId: string, patch: Partial<IvrNodeData>) => void;
  /** Remove a node and any edges touching it. */
  onRemove: (nodeId: string) => void;
  /** Look up the React-Flow node by id (for nodes that need siblings). */
  getNode: (nodeId: string) => IvrNode | undefined;
}

export const IvrEditorContext = createContext<IvrEditorContextValue | null>(null);

export function useIvrEditor(): IvrEditorContextValue {
  const ctx = useContext(IvrEditorContext);
  if (!ctx) {
    throw new Error('useIvrEditor must be used inside <IvrEditorContext.Provider>');
  }
  return ctx;
}